import {
  FICHIERS_CV_PAR_SELECTION_MAX,
  MESSAGES_FORMAT_CV,
  TAILLE_CV_MAX,
  type BilanCompletionCv,
  type ChampCoordonneeCv,
  type ReponseDepotCv,
} from '@rh/shared';
import { appel } from './api';
import { nomComplet } from './format';

/**
 * Dépôt de CV depuis le navigateur (docs/UI.md § CV à rattacher) : contrôles
 * locaux, classement des erreurs et messages. Le format réel est décidé par
 * l'API sur les octets ; le navigateur n'écarte que ce qui partirait pour rien
 * (fichier vide, trop lourd, photo HEIC).
 */

export type EtatEnvoiCv = 'en_file' | 'envoi' | 'termine' | 'refuse' | 'erreur_reseau';

export interface EnvoiCv {
  cle: string;
  fichier: File;
  etat: EtatEnvoiCv;
  message: string | null;
  /** Un nouvel essai peut aboutir : réseau coupé, envoi interrompu, panne du serveur. */
  reessayable: boolean;
  reponse: ReponseDepotCv | null;
}

export const LIBELLES_ETAT_ENVOI_CV: Record<EtatEnvoiCv, string> = {
  en_file: 'En file',
  envoi: 'Envoi…',
  termine: 'Envoyé',
  refuse: 'Refusé',
  erreur_reseau: 'Réseau coupé',
};

/** Un CV de 10 Mo sur une connexion mobile lente : l'aller-retour peut être long. */
export const DELAI_ENVOI_CV_MS = 300_000;
export const MESSAGE_CV_TROP_LOURD = 'Plus de 10 Mo : enregistrez le CV en PDF plus léger';
export const MESSAGE_RESEAU_CV = 'Réseau indisponible : réessayez l’envoi';
export const MESSAGE_ENVOI_IMPOSSIBLE = 'Envoi impossible : réessayez';

/** Motif de refus local, ou null si le fichier peut partir. */
export function controleLocalCv(fichier: { size: number; name?: string; type?: string }): string | null {
  if (fichier.size === 0) return MESSAGES_FORMAT_CV.vide;
  if (fichier.size > TAILLE_CV_MAX) return MESSAGE_CV_TROP_LOURD;
  if (/^image\/hei[cf]/i.test(fichier.type ?? '') || /\.hei[cf]$/i.test(fichier.name ?? '')) {
    return MESSAGES_FORMAT_CV.heic;
  }
  return null;
}

let numeroEnvoi = 0;

/**
 * Lignes d'envoi d'une sélection : 20 fichiers au plus (les suivants sont
 * comptés, pas envoyés) ; un fichier refusé localement ne part pas.
 */
export function preparerEnvois(fichiers: ArrayLike<File>): { envois: EnvoiCv[]; ignores: number } {
  const liste = Array.from(fichiers);
  const retenus = liste.slice(0, FICHIERS_CV_PAR_SELECTION_MAX);
  const envois = retenus.map((fichier): EnvoiCv => {
    const refus = controleLocalCv(fichier);
    numeroEnvoi += 1;
    return {
      cle: `envoi-cv-${numeroEnvoi}`,
      fichier,
      etat: refus ? 'refuse' : 'en_file',
      message: refus,
      reessayable: false,
      reponse: null,
    };
  });
  return { envois, ignores: liste.length - retenus.length };
}

export interface ClassementEnvoiCv {
  etat: 'refuse' | 'erreur_reseau';
  message: string;
  reessayable: boolean;
}

/**
 * Classe l'échec d'un envoi. Les classes d'erreur de `api.ts` se reconnaissent
 * par leur nom (un module simulé en test garde les mêmes noms) :
 * - `ErreurApi` : refus jugé par l'API, message déjà en français ;
 * - `ErreurReseau` 413 : corps refusé par un intermédiaire, sans JSON ;
 * - autre `ErreurReseau` ou `TypeError` : réseau ;
 * - abandon voulu (démontage) : null, rien à afficher.
 */
export function messageEnvoiCv(erreur: unknown): ClassementEnvoiCv | null {
  const e = erreur as { name?: unknown; message?: unknown; statut?: unknown; code?: unknown } | null;
  const nom = typeof e?.name === 'string' ? e.name : '';
  if (nom === 'AbortError') return null;
  if (nom === 'ErreurApi') {
    const statut = typeof e?.statut === 'number' ? e.statut : 0;
    return {
      etat: 'refuse',
      message: typeof e?.message === 'string' && e.message ? e.message : MESSAGE_ENVOI_IMPOSSIBLE,
      reessayable: e?.code === 'CV_ENVOI_INVALIDE' || statut >= 500,
    };
  }
  if (nom === 'ErreurReseau' && e?.statut === 413) {
    return { etat: 'refuse', message: MESSAGE_CV_TROP_LOURD, reessayable: false };
  }
  if (nom === 'ErreurReseau' || erreur instanceof TypeError) {
    return { etat: 'erreur_reseau', message: MESSAGE_RESEAU_CV, reessayable: true };
  }
  return { etat: 'erreur_reseau', message: MESSAGE_ENVOI_IMPOSSIBLE, reessayable: true };
}

/** Message d'une réponse de dépôt, page ou fiche. */
export function messageResultatDepot(reponse: ReponseDepotCv): string {
  switch (reponse.resultat) {
    case 'depose':
      return 'Déposé : lecture du CV en cours';
    case 'deja_en_attente':
      return 'Déjà déposé : ce CV attend dans la liste';
    case 'deja_rattache': {
      const fiche = reponse.aussiSur[0];
      return fiche
        ? `Déjà sur la fiche de ${nomComplet(fiche)} — ${fiche.annonceIntitule}`
        : 'Ce CV est déjà sur une fiche';
    }
    case 'rattache':
      return 'CV déposé sur cette candidature';
    case 'rattache_depot_existant':
      return 'CV déposé : il attendait dans « CV à rattacher », il est maintenant sur cette candidature';
    case 'deja_present':
      return 'Ce CV est déjà sur cette candidature';
  }
}

const LIBELLES_CHAMP: Record<ChampCoordonneeCv, string> = { telephone: 'Téléphone', email: 'E-mail' };

/** « Téléphone ajouté à la fiche. E-mail non ajouté : … » ; null sans complétion demandée. */
export function messageCompletion(bilan: BilanCompletionCv | null): { texte: string; alerte: boolean } | null {
  if (!bilan || (bilan.ajoutes.length === 0 && bilan.refus.length === 0)) return null;
  const phrases = [
    ...bilan.ajoutes.map((champ) => `${LIBELLES_CHAMP[champ]} ajouté à la fiche.`),
    ...bilan.refus.map((refus) => `${LIBELLES_CHAMP[refus.champ]} non ajouté : ${refus.message}.`),
  ];
  return { texte: phrases.join(' '), alerte: bilan.refus.length > 0 };
}

/** `2048` → « 2 ko », `1572864` → « 1,5 Mo ». */
export function formaterTaille(octets: number): string {
  if (octets < 1024) return `${octets} o`;
  if (octets < 1024 * 1024) return `${Math.round(octets / 1024)} ko`;
  return `${(octets / (1024 * 1024)).toFixed(1).replace('.', ',')} Mo`;
}

const MOIS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

function jourEtMois(date: Date): { jour: string; mois: string; annee: number } {
  const j = date.getUTCDate();
  return { jour: j === 1 ? '1er' : String(j), mois: MOIS[date.getUTCMonth()]!, annee: date.getUTCFullYear() };
}

/**
 * La purge de 2 h 30 efface le CV le jour `expireLe` (AAAA-MM-JJ) : « dans la
 * nuit du 28 au 29 septembre », « dans la nuit du 30 septembre au 1er octobre ».
 */
export function libelleEffacement(expireLe: string): string {
  const [a, m, j] = expireLe.split('-').map(Number) as [number, number, number];
  const fin = new Date(Date.UTC(a, m - 1, j));
  const debut = new Date(fin.getTime() - 86_400_000);
  const d = jourEtMois(debut);
  const f = jourEtMois(fin);
  if (d.mois === f.mois) return `dans la nuit du ${d.jour} au ${f.jour} ${f.mois}`;
  if (d.annee === f.annee) return `dans la nuit du ${d.jour} ${d.mois} au ${f.jour} ${f.mois}`;
  return `dans la nuit du ${d.jour} ${d.mois} ${d.annee} au ${f.jour} ${f.mois} ${f.annee}`;
}

/**
 * Envoie un fichier (champ `fichier`) : page `/cv-a-rattacher` ou fiche
 * `/candidatures/:id/documents`. `appel` laisse le navigateur poser l'en-tête
 * multipart ; le jeton se renouvelle et la demande se rejoue comme toute autre.
 */
export function envoyerCv(chemin: string, fichier: File, signal?: AbortSignal): Promise<ReponseDepotCv> {
  const donnees = new FormData();
  // Sans troisième argument : le même objet File part (une copie redépose exactement ce fichier).
  donnees.append('fichier', fichier);
  return appel<ReponseDepotCv>(chemin, { method: 'POST', body: donnees, delaiMs: DELAI_ENVOI_CV_MS, signal });
}
